import React, { useState, useEffect } from "react";
import StatusPill from "../components/common/StatusPill";
import { useParams, useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Clock,
  Stethoscope,
  Heart,
  Activity,
  Award,
  GraduationCap,
  FileText,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import LoadingSpinner from "../components/common/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import InfoItem from "../components/common/InfoItem";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { getDocument } from "../firebase/firestore";

const Section = ({ title, children }) => (
  <Card>
    <CardHeader className="pb-4">
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{children}</div>
    </CardContent>
  </Card>
);

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getDocument("Users", userId);

      if (result.success) {
        setUser(result.data);
      } else {
        throw new Error(result.error || "User not found");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching user details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => navigate(-1);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleDateString();
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="User profile" onBack={handleGoBack} />
        <LoadingSpinner message="Loading user details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader title="User profile" onBack={handleGoBack} />
        <Card className="mx-auto max-w-md">
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <h3 className="text-lg font-semibold">Error loading user</h3>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button onClick={fetchUserDetails}>
              <RefreshCw /> Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div>
        <PageHeader title="User profile" onBack={handleGoBack} />
        <Card className="mx-auto max-w-md">
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">User not found</h3>
            <p className="text-sm text-muted-foreground">The requested user could not be found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const userType = user.userType || "user";
  const TypeIcon = userType === "doctor" ? Stethoscope : userType === "patient" ? Heart : User;

  let verification = { icon: AlertCircle, text: "Pending", tone: "amber" };
  if (user.isVerified === true) {
    verification = { icon: CheckCircle, text: "Verified", tone: "green" };
  } else if (user.isVerified === false) {
    verification = { icon: XCircle, text: "Not verified", tone: "red" };
  }
  const VerifyIcon = verification.icon;

  return (
    <div className="space-y-5">
      <PageHeader
        title="User profile"
        description={`ID: ${user.id || userId}`}
        onBack={handleGoBack}
      />

      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 p-6">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
            <TypeIcon size={28} />
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-xl font-semibold">{user.name || user.displayName || "N/A"}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusPill style={{ textTransform: "capitalize" }}>{userType}</StatusPill>
              <StatusPill tone={verification.tone} icon={VerifyIcon}>{verification.text}</StatusPill>
            </div>
          </div>
        </CardContent>
      </Card>

      <Section title="Contact information">
        <InfoItem icon={Mail} label="Email" value={user.email || "N/A"} />
        <InfoItem icon={Phone} label="Phone" value={user.phoneNumber || user.phone || "N/A"} />
        {user.address && <InfoItem icon={MapPin} label="Address" value={user.address} />}
      </Section>

      {(user.dateOfBirth || user.age || user.gender) && (
        <Section title="Personal information">
          {user.dateOfBirth && <InfoItem icon={Calendar} label="Date of birth" value={formatDate(user.dateOfBirth)} />}
          {user.age && <InfoItem icon={Clock} label="Age" value={`${user.age} years old`} />}
          {user.gender && <InfoItem icon={User} label="Gender" value={user.gender} />}
        </Section>
      )}

      {userType === "doctor" && (
        <Section title="Professional information">
          {user.specialty && <InfoItem icon={Activity} label="Specialty" value={user.specialty} />}
          {user.experience && <InfoItem icon={Award} label="Experience" value={`${user.experience} years`} />}
          {user.qualification && <InfoItem icon={GraduationCap} label="Qualification" value={user.qualification} />}
          {user.licenseNumber && <InfoItem icon={Shield} label="License number" value={user.licenseNumber} />}
        </Section>
      )}

      {userType === "patient" && (
        <Section title="Medical information">
          {user.medicalHistory && <InfoItem icon={FileText} label="Medical history" value={user.medicalHistory} className="sm:col-span-2 xl:col-span-3" />}
          {user.currentMedications && <InfoItem icon={FileText} label="Current medications" value={user.currentMedications} className="sm:col-span-2 xl:col-span-3" />}
          {user.allergies && <InfoItem icon={AlertCircle} label="Allergies" value={user.allergies} className="sm:col-span-2 xl:col-span-3" />}
        </Section>
      )}

      <Section title="Account information">
        <InfoItem icon={Calendar} label="Joined" value={formatDateTime(user.createdAt)} />
        <InfoItem icon={Clock} label="Last updated" value={formatDateTime(user.updatedAt)} />
        <InfoItem icon={Shield} label="Account status" value={user.isActive !== false ? "Active" : "Inactive"} />
      </Section>
    </div>
  );
};

export default UserDetails;
